import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dropdown,
  Form,
  Header,
  Icon,
  Label,
  Loader,
  Message,
  Pagination,
  Segment,
  Table,
} from 'semantic-ui-react';

import {
  API,
  isRoot,
  showError,
  showFormalPricingWarningsV87,
  showSuccess,
} from '../../helpers';
import PageSizeSelectV8, {
  readPageSizeV8,
} from '../../components/PageSizeSelectV8';
import BillingDateSelectionV89 from './BillingDateSelectionV89';
import { buildLedgerUsageV815 } from './billingLedgerUsageV815';
import {
  buildBillingAggregateCostV822,
  buildBillingLedgerCostV822,
} from './billingCostStateV822';
import { renderQuota } from '../../helpers/render';
import {
  CustomerMonthlyInvoicesV98,
  RootMonthlyInvoicesV98,
} from './MonthlyInvoicesV98';
import './Billing.css';

const formatTime = (timestamp) => {
  if (!timestamp) return '-';
  return new Date(Number(timestamp) * 1000).toLocaleString();
};

const formatPercent = (basisPoints) => {
  const value = Number(basisPoints || 0) / 100;
  return value.toFixed(value % 1 === 0 ? 0 : 2) + '%';
};

const Metric = ({ label, value, hint }) => (
  <div className='billing-metric-v8'>
    <span className='billing-metric-label-v8'>{label}</span>
    <strong className='billing-metric-value-v8'>{value ?? '-'}</strong>
    {hint ? <span className='billing-metric-hint-v8'>{hint}</span> : null}
  </div>
);

export const LegacySummaryBandV86 = ({ user, t }) => {
  if (!user) return null;
  return (
    <section className='billing-summary-band-v8'>
      <div className='billing-summary-heading-v8'>
        <div>
          <Header as='h2'>{t('home.overview.personal_account')}</Header>
          <span className='billing-muted-v8'>
            {t('billing.legacy_description')}
          </span>
        </div>
        <Label basic>{t('billing.legacy_mode')}</Label>
      </div>
      <div className='billing-metrics-v8 billing-legacy-metrics-v86'>
        <Metric
          label={t('billing.metrics.available_credit')}
          value={renderQuota(user.quota, t)}
        />
        <Metric
          label={t('billing.metrics.used_total')}
          value={renderQuota(user.used_quota, t)}
        />
        <Metric
          label={t('billing.metrics.requests_total')}
          value={new Intl.NumberFormat().format(
            Number(user.request_count) || 0
          )}
        />
      </div>
    </section>
  );
};

export const SummaryBand = ({ summary, internal, t }) => {
  if (!summary?.configured) return null;
  if (summary.scope === 'member') {
    const unlimited = summary.credit_limit === 'unlimited';
    return (
      <section className='billing-summary-band-v8'>
        <div className='billing-summary-heading-v8'>
          <div>
            <Header as='h2'>{summary.account?.name}</Header>
            <span className='billing-muted-v8'>
              {formatTime(summary.period_start)} -{' '}
              {formatTime(summary.period_end)}
            </span>
          </div>
          <div className='billing-labels-v8'>
            <Label
              basic
              color={summary.membership?.status === 'active' ? 'green' : 'red'}
            >
              {t('billing.status.' + summary.membership?.status)}
            </Label>
            <Label basic>{t('billing.mode.' + summary.account?.mode)}</Label>
          </div>
        </div>
        <div className='billing-metrics-v8'>
          <Metric
            label={t('billing.metrics.available_credit')}
            value={
              unlimited
                ? t('billing.unlimited')
                : 'USD ' + summary.available_credit_usd
            }
            hint={t('billing.mode.' + summary.account?.mode)}
          />
          <Metric
            label={t('billing.metrics.member_used')}
            value={'USD ' + summary.cycle_request_charges_usd}
          />
          <Metric
            label={t('billing.metrics.member_limit')}
            value={
              summary.member_cycle_limit_usd == null
                ? t('billing.unlimited')
                : 'USD ' + summary.member_cycle_limit_usd
            }
          />
          <Metric
            label={t('billing.metrics.list_amount')}
            value={'USD ' + summary.cycle_list_amount_usd}
            hint={'CNY ' + summary.cycle_list_amount_cny}
          />
        </div>
      </section>
    );
  }
  const discount = summary.discount || {};
  const prepaid = summary.account?.mode === 'prepaid';
  const cost = internal
    ? buildBillingAggregateCostV822(summary, 'summary', t)
    : null;
  return (
    <section className='billing-summary-band-v8'>
      <div className='billing-summary-heading-v8'>
        <div>
          <Header as='h2'>{summary.account?.name}</Header>
          <span className='billing-muted-v8'>
            {formatTime(summary.period_start)} -{' '}
            {formatTime(summary.period_end)}
          </span>
        </div>
        <div className='billing-labels-v8'>
          <Label
            basic
            color={summary.account?.status === 'active' ? 'green' : 'red'}
          >
            {t('billing.status.' + summary.account?.status)}
          </Label>
          <Label basic>{t('billing.mode.' + summary.account?.mode)}</Label>
          <Label
            basic
            color={
              summary.account?.enforcement_mode === 'formal'
                ? 'orange'
                : summary.account?.enforcement_mode === 'internal'
                ? 'blue'
                : 'grey'
            }
          >
            {t('billing.enforcement.' + summary.account?.enforcement_mode)}
          </Label>
        </div>
      </div>
      <div className='billing-metrics-v8'>
        <Metric
          label={
            prepaid
              ? t('billing.metrics.available_credit')
              : t('billing.metrics.credit')
          }
          value={
            prepaid
              ? 'USD ' + summary.available_credit_usd
              : t('billing.unlimited')
          }
          hint={t('billing.mode.' + summary.account?.mode)}
        />
        <Metric
          label={t('billing.metrics.list_amount')}
          value={'USD ' + summary.cycle_list_amount_usd}
          hint={'CNY ' + summary.cycle_list_amount_cny}
        />
        <Metric
          label={t('billing.metrics.tier')}
          value={discount.tier_name || t('billing.tier.list')}
          hint={
            formatPercent(discount.payable_basis_points) +
            ' ' +
            t('billing.payable')
          }
        />
        <Metric
          label={t('billing.metrics.discount')}
          value={'USD ' + summary.cycle_provisional_discount_usd}
          hint={
            discount.mixed_discount_v1022
              ? t('setting.billing.immediate.segmented')
              : t('billing.provisional')
          }
        />
        <Metric
          label={t('billing.metrics.net')}
          value={'USD ' + summary.cycle_provisional_net_usd}
          hint={t('billing.provisional')}
        />
        {discount.next_tier_id && discount.next_tier_id !== '0' ? (
          <Metric
            label={t('billing.metrics.next_tier')}
            value={discount.next_tier_name}
            hint={t('billing.next_tier_remaining', {
              amount: discount.next_list_remaining_cny,
            })}
          />
        ) : (
          <Metric
            label={t('billing.metrics.next_tier')}
            value={t('billing.highest_tier')}
          />
        )}
        {internal ? (
          <>
            <Metric
              label={t('billing.metrics.provider_cost')}
              value={cost.value}
              hint={[
                cost.hint,
                t('billing.profitability.additional_attempt_cost_hint', {
                  amount: summary.cycle_additional_attempt_cost_usd,
                }),
              ]
                .filter(Boolean)
                .join(' ')}
            />
            <Metric
              label={t('billing.metrics.gross_profit')}
              value={cost.profitValue}
              hint={cost.profitHint}
            />
          </>
        ) : null}
      </div>
    </section>
  );
};

export const LedgerTable = ({ entries, internal, t }) => (
  <div className='management-table-scroll-v72' role='region' tabIndex={0}>
    <Table basic='very' compact unstackable className='management-table-v72'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('billing.table.time')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.table.type')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.table.model')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.table.usage')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.table.amount')}</Table.HeaderCell>
          {internal ? (
            <Table.HeaderCell>
              {t('billing.table.provider_cost')}
            </Table.HeaderCell>
          ) : null}
          <Table.HeaderCell>{t('billing.table.reference')}</Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {entries.map((entry) => {
          const usage = buildLedgerUsageV815(entry, internal, t);
          const cost = internal ? buildBillingLedgerCostV822(entry, t) : null;
          return (
            <Table.Row key={entry.id}>
              <Table.Cell>{formatTime(entry.created_at)}</Table.Cell>
              <Table.Cell>
                <Label basic>{t('billing.entry.' + entry.entry_type)}</Label>
              </Table.Cell>
              <Table.Cell>{entry.billed_model_name || '-'}</Table.Cell>
              <Table.Cell>
                <div className='billing-usage-v815'>
                  <span
                    className={
                      usage.hasImages ? 'billing-usage-primary-v815' : undefined
                    }
                  >
                    {usage.hasImages ? (
                      <Icon name='image outline' aria-hidden='true' />
                    ) : null}
                    {usage.primary}
                  </span>
                  {usage.secondary ? (
                    <span className='billing-cell-secondary-v8'>
                      {usage.secondary}
                    </span>
                  ) : null}
                </div>
              </Table.Cell>
              <Table.Cell>
                {entry.direction === 'credit' ? '+' : '-'} USD{' '}
                {entry.amount_usd}
              </Table.Cell>
              {internal ? (
                <Table.Cell>
                  {cost.value}
                  {cost.hint ? (
                    <span className='billing-cell-secondary-v8'>
                      {cost.hint}
                    </span>
                  ) : null}
                </Table.Cell>
              ) : null}
              <Table.Cell>
                <span
                  className='billing-reference-v8'
                  title={entry.request_id || entry.reference_id}
                >
                  {entry.request_id || entry.reference_id || '-'}
                </span>
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  </div>
);

export const StatementTable = ({ statements, internal, t }) => (
  <div className='management-table-scroll-v72' role='region' tabIndex={0}>
    <Table basic='very' compact unstackable className='management-table-v72'>
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell>{t('billing.statement.period')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.statement.list')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.statement.discount')}</Table.HeaderCell>
          <Table.HeaderCell>{t('billing.statement.payable')}</Table.HeaderCell>
          {internal ? (
            <Table.HeaderCell>{t('billing.statement.profit')}</Table.HeaderCell>
          ) : null}
          <Table.HeaderCell>
            {t('billing.statement.finalized')}
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {statements.map((statement) => {
          const cost = internal
            ? buildBillingAggregateCostV822(statement, 'statement', t)
            : null;
          return (
            <Table.Row key={statement.id}>
              <Table.Cell>
                {formatTime(statement.period_start)} -{' '}
                {formatTime(statement.period_end)}
              </Table.Cell>
              <Table.Cell>
                USD {statement.list_amount_usd}
                <span className='billing-cell-secondary-v8'>
                  CNY {statement.list_amount_cny}
                </span>
              </Table.Cell>
              <Table.Cell>
                {statement.mixed_discount_v1022
                  ? t('setting.billing.immediate.segmented')
                  : formatPercent(statement.payable_basis_points)}
                <span className='billing-cell-secondary-v8'>
                  - USD {statement.discount_amount_usd}
                </span>
              </Table.Cell>
              <Table.Cell>
                <strong>USD {statement.payable_amount_usd}</strong>
              </Table.Cell>
              {internal ? (
                <Table.Cell>
                  {cost.profitValue}
                  {cost.profitHint ? (
                    <span className='billing-cell-secondary-v8'>
                      {cost.profitHint}
                    </span>
                  ) : null}
                </Table.Cell>
              ) : null}
              <Table.Cell>
                {formatTime(statement.finalized_at)}
                {statement.revision_number > 0 ? (
                  <span className='billing-cell-secondary-v8'>
                    <Label basic color='orange' size='tiny'>
                      {t('billing.calendar.period_status.restated')} v
                      {statement.revision_number}
                    </Label>
                    {statement.restated_at
                      ? formatTime(statement.restated_at)
                      : null}
                  </span>
                ) : null}
              </Table.Cell>
            </Table.Row>
          );
        })}
      </Table.Body>
    </Table>
  </div>
);

export const DataSection = ({ title, empty, children }) => (
  <section className='billing-data-section-v8'>
    <Header as='h3'>{title}</Header>
    {empty ? <Message info content={empty} /> : children}
  </section>
);

const CustomerBilling = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selfUser, setSelfUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [statements, setStatements] = useState([]);
  const [ledgerPage, setLedgerPage] = useState(1);
  const [ledgerPageSize, setLedgerPageSize] = useState(() =>
    readPageSizeV8('v8-page-size-billing-ledger', 50)
  );
  const [ledgerTotal, setLedgerTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [summaryResponse, ledgerResponse, statementResponse, userResponse] =
        await Promise.all([
          API.get('/api/billing/v8/self/summary'),
          API.get(
            '/api/billing/v8/self/ledger?page=' +
              ledgerPage +
              '&page_size=' +
              ledgerPageSize
          ),
          API.get('/api/billing/v8/self/statements?page=1&page_size=50'),
          API.get('/api/user/self'),
        ]);
      if (
        !summaryResponse ||
        !ledgerResponse ||
        !statementResponse ||
        !userResponse
      )
        return;
      if (!summaryResponse.data?.success)
        throw new Error(summaryResponse.data?.message);
      if (!ledgerResponse.data?.success)
        throw new Error(ledgerResponse.data?.message);
      if (!statementResponse.data?.success)
        throw new Error(statementResponse.data?.message);
      if (!userResponse.data?.success)
        throw new Error(userResponse.data?.message);
      setSummary(summaryResponse.data.data);
      setSelfUser(userResponse.data.data);
      setEntries(ledgerResponse.data.data?.entries || []);
      setLedgerTotal(ledgerResponse.data.data?.total || 0);
      setStatements(statementResponse.data.data?.statements || []);
    } catch (error) {
      showError(error.message || t('billing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [ledgerPage, ledgerPageSize, t]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loader active inline='centered' />;
  if (!summary?.configured) {
    return selfUser ? (
      <LegacySummaryBandV86 user={selfUser} t={t} />
    ) : (
      <Message info content={t('billing.not_configured')} />
    );
  }

  return (
    <>
      <SummaryBand summary={summary} internal={false} t={t} />
      <CustomerMonthlyInvoicesV98 />
      <BillingDateSelectionV89
        endpoint='/api/billing/v8/self/date-selection-summary'
        billingDay={summary.account?.billing_day || 1}
      />
      <DataSection
        title={t('billing.ledger.title')}
        empty={entries.length ? '' : t('billing.ledger.empty')}
      >
        <LedgerTable entries={entries} internal={false} t={t} />
        <div className='list-pagination-v8'>
          <PageSizeSelectV8
            label={t('log.table.page_size')}
            storageKey='v8-page-size-billing-ledger'
            value={ledgerPageSize}
            onChange={(nextPageSize) => {
              setLedgerPage(1);
              setLedgerPageSize(nextPageSize);
            }}
          />
          {ledgerTotal > ledgerPageSize ? (
            <Pagination
              activePage={ledgerPage}
              totalPages={Math.ceil(ledgerTotal / ledgerPageSize)}
              onPageChange={(_, { activePage }) =>
                setLedgerPage(Number(activePage))
              }
            />
          ) : null}
        </div>
      </DataSection>
      {summary.scope !== 'member' ? (
        <DataSection
          title={t('billing.statement.title')}
          empty={statements.length ? '' : t('billing.statement.empty')}
        >
          <StatementTable statements={statements} internal={false} t={t} />
        </DataSection>
      ) : null}
    </>
  );
};

const AdminControls = ({
  accountId,
  summary,
  entries,
  statements,
  events,
  reload,
  t,
}) => {
  const [enforcement, setEnforcement] = useState({
    mode: summary?.account?.enforcement_mode || 'frozen',
    note: '',
  });
  const [saving, setSaving] = useState('');
  const [period, setPeriod] = useState('');

  useEffect(() => {
    setEnforcement({
      mode: summary?.account?.enforcement_mode || 'frozen',
      note: '',
    });
  }, [summary?.account?.enforcement_mode]);

  const duePeriods = useMemo(() => {
    const finalized = new Set(
      statements.map((statement) => Number(statement.period_start))
    );
    const unique = new Map();
    entries.forEach((entry) => {
      const start = Number(entry.billing_period_start);
      const end = Number(entry.billing_period_end);
      if (start > 0 && end > 0 && end <= Date.now() / 1000) {
        unique.set(start, {
          key: start,
          value: String(start),
          text: formatTime(start),
        });
      }
    });
    return Array.from(unique.values()).filter(
      (item) => !finalized.has(item.key)
    );
  }, [entries, statements]);

  const saveEnforcement = async () => {
    if (!enforcement.note.trim()) {
      showError(t('billing.errors.enforcement_note'));
      return;
    }
    setSaving('enforcement');
    try {
      const response = await API.post(
        '/api/billing/v8/admin/accounts/' + accountId + '/enforcement',
        {
          enforcement_mode: enforcement.mode,
          legacy_quota_reconciled: false,
          note: enforcement.note,
        }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showFormalPricingWarningsV87(response.data?.pricing_warnings, t);
      showSuccess(t('billing.messages.enforcement_updated'));
      await reload();
    } catch (error) {
      showError(error.message || t('billing.errors.enforcement'));
    } finally {
      setSaving('');
    }
  };

  const finalize = async () => {
    if (!period) return;
    setSaving('statement');
    try {
      const response = await API.post(
        '/api/billing/v8/admin/accounts/' + accountId + '/statements/finalize',
        { period_start: Number(period) }
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(t('billing.messages.finalized'));
      setPeriod('');
      await reload();
    } catch (error) {
      showError(error.message || t('billing.errors.finalize'));
    } finally {
      setSaving('');
    }
  };

  return (
    <section className='billing-admin-controls-v8'>
      <div className='billing-control-v8'>
        <Header as='h3'>{t('billing.admin.enforcement')}</Header>
        <Form>
          <Form.Select
            label={t('billing.admin.mode')}
            value={enforcement.mode}
            options={[
              {
                key: 'frozen',
                value: 'frozen',
                text: t('billing.enforcement.frozen'),
              },
              {
                key: 'internal',
                value: 'internal',
                text: t('billing.enforcement.internal'),
              },
              {
                key: 'formal',
                value: 'formal',
                text: t('billing.enforcement.formal'),
              },
            ]}
            onChange={(_, { value }) =>
              setEnforcement({ ...enforcement, mode: value })
            }
          />
          <Message
            info
            content={t('billing.enforcement_description.' + enforcement.mode)}
          />
          <Form.Input
            label={t('billing.admin.note')}
            maxLength={191}
            value={enforcement.note}
            onChange={(_, { value }) =>
              setEnforcement({ ...enforcement, note: value })
            }
          />
          <Button
            color={enforcement.mode === 'formal' ? 'orange' : undefined}
            icon
            labelPosition='left'
            disabled={enforcement.mode === summary?.account?.enforcement_mode}
            loading={saving === 'enforcement'}
            onClick={saveEnforcement}
          >
            <Icon name='shield alternate' />
            {t('billing.admin.update_enforcement')}
          </Button>
        </Form>
      </div>

      <div className='billing-control-v8'>
        <Header as='h3'>{t('billing.admin.finalize')}</Header>
        <Form>
          <Form.Field>
            <label>{t('billing.admin.due_period')}</label>
            <Dropdown
              selection
              fluid
              placeholder={t('billing.admin.select_period')}
              options={duePeriods}
              value={period}
              onChange={(_, { value }) => setPeriod(value)}
              noResultsMessage={t('billing.admin.no_due_period')}
            />
          </Form.Field>
          <Button
            icon
            labelPosition='left'
            disabled={!period}
            loading={saving === 'statement'}
            onClick={finalize}
          >
            <Icon name='lock' />
            {t('billing.admin.finalize_action')}
          </Button>
        </Form>
      </div>

      <div className='billing-control-v8'>
        <Header as='h3'>{t('billing.admin.audit')}</Header>
        {events.length ? (
          <div className='billing-audit-list-v8'>
            {events.map((event) => (
              <div key={event.id} className='billing-audit-row-v8'>
                <span>{formatTime(event.created_at)}</span>
                <strong>
                  {t('billing.enforcement.' + event.previous_enforcement_mode)}{' '}
                  ? {t('billing.enforcement.' + event.new_enforcement_mode)}
                </strong>
                <span>{event.note}</span>
              </div>
            ))}
          </div>
        ) : (
          <span className='billing-muted-v8'>
            {t('billing.admin.no_audit')}
          </span>
        )}
      </div>
    </section>
  );
};

const RootBilling = () => {
  const { t } = useTranslation();
  const [organizations, setOrganizations] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loading, setLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [statements, setStatements] = useState([]);
  const [events, setEvents] = useState([]);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/admin/organizations?page=1&page_size=100'
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      const rows = (response.data.data?.organizations || []).filter(
        (organization) =>
          !organization.virtual &&
          organization.account?.id &&
          /^\d+$/.test(String(organization.account.id))
      );
      setOrganizations(rows);
      setSelectedAccount((current) =>
        rows.some((organization) => String(organization.account.id) === current)
          ? current
          : rows.length
          ? String(rows[0].account.id)
          : ''
      );
    } catch (error) {
      showError(error.message || t('billing.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const loadAccount = useCallback(async () => {
    if (!/^\d+$/.test(selectedAccount)) return;
    setAccountLoading(true);
    try {
      const base = '/api/billing/v8/admin/accounts/' + selectedAccount;
      const [
        summaryResponse,
        ledgerResponse,
        statementResponse,
        eventResponse,
      ] = await Promise.all([
        API.get(base + '/summary'),
        API.get(base + '/ledger?page=1&page_size=100'),
        API.get(base + '/statements?page=1&page_size=100'),
        API.get(base + '/enforcement-events?page=1&page_size=50'),
      ]);
      if (
        !summaryResponse ||
        !ledgerResponse ||
        !statementResponse ||
        !eventResponse
      )
        return;
      if (!summaryResponse.data?.success)
        throw new Error(summaryResponse.data?.message);
      setSummary(summaryResponse.data.data);
      setEntries(ledgerResponse.data?.data?.entries || []);
      setStatements(
        Array.isArray(statementResponse.data?.data)
          ? statementResponse.data.data
          : []
      );
      setEvents(
        Array.isArray(eventResponse.data?.data) ? eventResponse.data.data : []
      );
    } catch (error) {
      showError(error.message || t('billing.errors.load'));
    } finally {
      setAccountLoading(false);
    }
  }, [selectedAccount, t]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const organizationOptions = organizations.map((organization) => ({
    key: organization.account.id,
    value: String(organization.account.id),
    text:
      organization.account.name +
      ' (' +
      (organization.owner?.username || '-') +
      ')',
  }));

  if (loading) return <Loader active inline='centered' />;

  return (
    <>
      <RootMonthlyInvoicesV98 />
      <div className='billing-admin-toolbar-v8'>
        <div>
          <Header as='h2'>{t('billing.admin.title')}</Header>
          <span className='billing-muted-v8'>
            {t('billing.admin.subtitle')}
          </span>
        </div>
        <Dropdown
          search
          selection
          options={organizationOptions}
          value={selectedAccount}
          placeholder={t('billing.admin.select_account')}
          onChange={(_, { value }) => setSelectedAccount(value)}
        />
      </div>
      {!selectedAccount ? (
        <Message info content={t('billing.admin.no_accounts')} />
      ) : accountLoading ? (
        <Loader active inline='centered' />
      ) : (
        <>
          <SummaryBand summary={summary} internal={false} t={t} />
          <AdminControls
            accountId={selectedAccount}
            summary={summary}
            entries={entries}
            statements={statements}
            events={events}
            reload={loadAccount}
            t={t}
          />
          <DataSection
            title={t('billing.ledger.title')}
            empty={entries.length ? '' : t('billing.ledger.empty')}
          >
            <LedgerTable entries={entries} internal={false} t={t} />
          </DataSection>
          <DataSection
            title={t('billing.statement.title')}
            empty={statements.length ? '' : t('billing.statement.empty')}
          >
            <StatementTable statements={statements} internal={false} t={t} />
          </DataSection>
        </>
      )}
    </>
  );
};

const Billing = () => {
  const root = useMemo(() => isRoot(), []);
  const { t } = useTranslation();
  return (
    <div className='dashboard-container management-container-v4 billing-page-v8'>
      {!root ? (
        <div className='billing-page-heading-v8'>
          <Header as='h2'>{t('billing.title')}</Header>
          <span className='billing-muted-v8'>{t('billing.subtitle')}</span>
        </div>
      ) : null}
      <Segment basic className='billing-content-v8'>
        {root ? <RootBilling /> : <CustomerBilling />}
      </Segment>
    </div>
  );
};

export default Billing;
